import { createContext, useContext, useState, type ReactNode } from 'react';

import { detectUnsavedChanges } from 'src/modules/detect-unsaved-changes';

export const CAREER_HISTORY_FORM = 'career-history-form';
export const PROFILE_INSIGHTS_FORM = 'profile-insights-form';
export const EDUCATION_FORM = 'education-form';
export const LANGUAGES_FORM = 'languages-form';
export const LICENCES_FORM = 'licences-form';
export const NEXT_ROLE_APPROACHABILITY_FORM = 'next-role-approachability-form';
export const NEXT_ROLE_AVAILABILITY_FORM = 'next-role-availability-form';
export const NEXT_ROLE_CLASSIFICATION_FORM = 'next-role-classification-form';
export const NEXT_ROLE_PREFERRED_LOCATION_FORM =
  'next-role-preferred-location-form';
export const NEXT_ROLE_RIGHT_TO_WORK_FORM = 'next-role-right-to-work-form';
export const NEXT_ROLE_SALARY_FORM = 'next-role-salary-form';
export const NEXT_ROLE_WORK_TYPE_FORM = 'next-role-work-type-form';
export const PERSONAL_DETAILS_FORM = 'personal-details-form';
export const PREFERRED_LOCATION_FORM = 'preferred-location-form';
export const REPORT_PROFILE_FORM = 'report-profile-form';
export const RESUME_FORM = 'resume-form';
export const SKILLS_FORM = 'skills-form';
export const SUMMARY_FORM = 'summary-form';

export const FORMS = [
  CAREER_HISTORY_FORM,
  PROFILE_INSIGHTS_FORM,
  EDUCATION_FORM,
  LANGUAGES_FORM,
  LICENCES_FORM,
  NEXT_ROLE_APPROACHABILITY_FORM,
  NEXT_ROLE_AVAILABILITY_FORM,
  NEXT_ROLE_CLASSIFICATION_FORM,
  NEXT_ROLE_PREFERRED_LOCATION_FORM,
  NEXT_ROLE_RIGHT_TO_WORK_FORM,
  NEXT_ROLE_SALARY_FORM,
  NEXT_ROLE_WORK_TYPE_FORM,
  PERSONAL_DETAILS_FORM,
  PREFERRED_LOCATION_FORM,
  REPORT_PROFILE_FORM,
  RESUME_FORM,
  SKILLS_FORM,
  SUMMARY_FORM,
] as const;

export type Form = (typeof FORMS)[number];

export type State = Record<Form, { dirty: boolean }>;

export const initialState: State = {
  [CAREER_HISTORY_FORM]: { dirty: false },
  [PROFILE_INSIGHTS_FORM]: { dirty: false },
  [EDUCATION_FORM]: { dirty: false },
  [LANGUAGES_FORM]: { dirty: false },
  [LICENCES_FORM]: { dirty: false },
  [NEXT_ROLE_APPROACHABILITY_FORM]: { dirty: false },
  [NEXT_ROLE_AVAILABILITY_FORM]: { dirty: false },
  [NEXT_ROLE_CLASSIFICATION_FORM]: { dirty: false },
  [NEXT_ROLE_PREFERRED_LOCATION_FORM]: { dirty: false },
  [NEXT_ROLE_RIGHT_TO_WORK_FORM]: { dirty: false },
  [NEXT_ROLE_SALARY_FORM]: { dirty: false },
  [NEXT_ROLE_WORK_TYPE_FORM]: { dirty: false },
  [PERSONAL_DETAILS_FORM]: { dirty: false },
  [PREFERRED_LOCATION_FORM]: { dirty: false },
  [REPORT_PROFILE_FORM]: { dirty: false },
  [RESUME_FORM]: { dirty: false },
  [SKILLS_FORM]: { dirty: false },
  [SUMMARY_FORM]: { dirty: false },
};

export type FormContextType = {
  setDirtyState: (form: Form, dirty: boolean) => void;
} & State;

const FormContext = createContext<FormContextType | undefined>(undefined);

export const FormProvider = ({ children }: { children: ReactNode }) => {
  const [formState, setFormState] = useState<State>({
    ...initialState,
  });

  detectUnsavedChanges(formState);

  const updateDirtyState = (form: Form, dirty: boolean) => {
    setFormState((prev) => ({
      ...prev,
      [form]: {
        dirty,
      },
    }));
  };

  return (
    <FormContext.Provider
      value={{
        ...formState,
        setDirtyState: updateDirtyState,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = (): FormContextType => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
};
